import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import MainLayout from '../../components/layout/MainLayout';

const ZonnepanelenPage = () => {
  const [activeFilter, setActiveFilter] = useState('alle');

  const specialists = [
    {
      name: 'SolarTech Solutions',
      rating: 4.9,
      reviews: 127,
      location: 'Amsterdam',
      specialization: 'Residentiële zonnepanelen',
      experience: '12+ jaar',
      image: '/assets/images/no_image.png'
    },
    {
      name: 'GreenEnergy Installateurs',
      rating: 4.8,
      reviews: 98,
      location: 'Rotterdam',
      specialization: 'Commerciële installaties',
      experience: '8+ jaar',
      image: '/assets/images/no_image.png'
    },
    {
      name: 'Zon & Co',
      rating: 4.7,
      reviews: 84,
      location: 'Utrecht',
      specialization: 'Zonnepanelen & batterijen',
      experience: '10+ jaar',
      image: '/assets/images/no_image.png'
    }
  ];

  const opdrachten = [
    {
      title: 'Zonnepanelen installatie woning',
      location: 'Amsterdam Noord',
      budget: '€8.000 - €12.000',
      deadline: '2 weken',
      description: '16 zonnepanelen op schuin dak, zuidelijke oriëntatie'
    },
    {
      title: 'Commerciële zonnepanelen project',
      location: 'Rotterdam',
      budget: '€45.000 - €60.000',
      deadline: '1 maand',
      description: 'Groot bedrijfspand, 120 panelen met monitoring systeem'
    },
    {
      title: 'Zonnepanelen met batterijopslag',
      location: 'Utrecht',
      budget: '€15.000 - €20.000',
      deadline: '3 weken',
      description: '20 panelen inclusief 10kWh thuisbatterij systeem'
    }
  ];

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <motion.section 
          className="bg-gradient-to-br from-amber-50 to-orange-50 py-16 md:py-24"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
                <Icon name="ArrowLeft" size={16} />
                Terug naar home
              </Link>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Icon name="Sun" size={32} className="text-amber-600" />
                </div>
                <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground">
                  Zonnepanelen Installatie
                </h1>
              </div>
              <p className="text-lg text-muted-foreground max-w-3xl">
                Vind ervaren specialisten voor professionele installatie van zonnepanelen. Van residentiële tot commerciële projecten.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Stats Section */}
        <section className="py-12 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: 'Actieve opdrachten', value: '47', icon: 'Briefcase' },
                { label: 'Specialisten', value: '156', icon: 'Users' },
                { label: 'Gemiddelde rating', value: '4.8', icon: 'Star' },
                { label: 'Voltooide projecten', value: '1.2k+', icon: 'CheckCircle' }
              ]?.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Icon name={stat?.icon} size={24} className="text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold text-foreground mb-1">{stat?.value}</div>
                  <div className="text-sm text-muted-foreground">{stat?.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Opdrachten Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-heading font-semibold text-foreground mb-8">
                Actuele Opdrachten
              </h2>
              <div className="grid gap-6">
                {opdrachten?.map((opdracht, index) => (
                  <motion.div
                    key={index}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6 transition-all"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-foreground mb-2">{opdracht?.title}</h3>
                        <p className="text-muted-foreground mb-3">{opdracht?.description}</p>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Icon name="MapPin" size={16} />
                            {opdracht?.location}
                          </span>
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Icon name="Calendar" size={16} />
                            {opdracht?.deadline}
                          </span>
                          <span className="flex items-center gap-1 text-primary font-medium">
                            <Icon name="DollarSign" size={16} />
                            {opdracht?.budget}
                          </span>
                        </div>
                      </div>
                      <Link 
                        to="/opdracht-zoeken"
                        className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap"
                      >
                        Bekijk details
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Specialisten Section */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-heading font-semibold text-foreground mb-8">
                Top Specialisten
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {specialists?.map((specialist, index) => (
                  <motion.div
                    key={index}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6 transition-all"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <img 
                        src={specialist?.image} 
                        alt={`${specialist?.name} - Zonnepanelen specialist`}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground mb-1">{specialist?.name}</h3>
                        <div className="flex items-center gap-1 text-sm">
                          <Icon name="Star" size={14} className="text-amber-500 fill-amber-500" />
                          <span className="font-medium">{specialist?.rating}</span>
                          <span className="text-muted-foreground">({specialist?.reviews})</span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Icon name="MapPin" size={16} />
                        {specialist?.location}
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Icon name="Award" size={16} />
                        {specialist?.specialization}
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Icon name="Briefcase" size={16} />
                        {specialist?.experience}
                      </div>
                    </div>
                    <Link 
                      to="/contractor-job-browser"
                      className="mt-4 w-full block text-center px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors"
                    >
                      Bekijk profiel
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12"
            >
              <Icon name="Sun" size={48} className="text-primary mx-auto mb-4" />
              <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
                Klaar om te starten?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Plaats uw opdracht en ontvang offertes van gekwalificeerde zonnepanelen specialisten
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/opdracht-plaatsen"
                  className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
                >
                  Opdracht plaatsen
                </Link>
                <Link 
                  to="/opdracht-zoeken"
                  className="px-8 py-3 border border-border bg-card text-foreground rounded-lg hover:bg-muted transition-colors font-medium"
                >
                  Alle opdrachten bekijken
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

export default ZonnepanelenPage;